import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

// Define the print job data type to fix TypeScript errors
/**
 * @typedef {Object} PrintJobData
 * @property {string} id - The print job ID
 * @property {string} [status] - The overall status
 * @property {string} [paymentStatus] - The payment workflow status
 * @property {string} [customerPaymentStatus] - Customer payment status
 * @property {string} [bookId] - Associated book ID
 * @property {number} [totalAmount] - Total payment amount
 * @property {string} [currency] - Payment currency
 */


function PrintJobPaymentStatusPage() {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState('loading');
  const [message, setMessage] = useState('');
  /** @type {[PrintJobData | null, React.Dispatch<React.SetStateAction<PrintJobData | null>>]} */
  const [printJob, setPrintJob] = useState(null);

  useEffect(() => {
    // First check if we have a success or canceled parameter in the URL
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    // client_secret no longer used; we rely on server-side webhook
    
    // Fetch the print job details regardless of payment status
    const fetchPrintJob = async () => {
      try {
        const db = getFirestore();
        const jobDoc = await getDoc(doc(db, 'printJobs', jobId));
        
        if (!jobDoc.exists()) {
          console.error('Print job not found');
          return null;
        }
        
        /** @type {PrintJobData} */
        const jobData = { id: jobDoc.id, ...jobDoc.data() };
        setPrintJob(jobData);
        
        // If the job shows as paid in Firestore, show success regardless
        if (jobData && (
            (typeof jobData.status === 'string' && jobData.status === 'paid') || 
            (typeof jobData.paymentStatus === 'string' && jobData.paymentStatus === 'paid') || 
            (typeof jobData.paymentStatus === 'string' && jobData.paymentStatus === 'PAID_AWAITING_FULFILLMENT') || 
            (typeof jobData.customerPaymentStatus === 'string' && jobData.customerPaymentStatus === 'paid')
        )) {
          setPaymentStatus('success');
          setMessage('Payment successful! Your print job is now being processed.');
          return jobData;
        }
        
        return jobData;
      } catch (error) {
        console.error('Error fetching print job:', error);
        return null;
      }
    };
    
    // Function to handle success parameter
    const handleSuccessParam = async () => {
      const jobData = await fetchPrintJob();
      
      // If success param is true and job is not marked as paid, update it
      if (success === 'true' && jobData && 
          (typeof jobData.status !== 'string' || jobData.status !== 'paid') && 
          (typeof jobData.paymentStatus !== 'string' || jobData.paymentStatus !== 'PAID_AWAITING_FULFILLMENT')) {
        try {
          // Manually update the print job status since webhook might have failed
          const db = getFirestore();
          await updateDoc(doc(db, 'printJobs', jobId), {
            status: 'paid',
            paymentStatus: 'PAID_AWAITING_FULFILLMENT',
            customerPaymentStatus: 'paid',
            updatedAt: new Date()
          });
          
          setPaymentStatus('success');
          setMessage('Payment successful! Your print job is now being processed.');
        } catch (updateError) {
          console.error('Error updating print job status:', updateError);
        }
      } else if (success === 'true') {
        setPaymentStatus('success');
        setMessage('Payment successful! Your print job is now being processed.');
      }
    };
    
    const init = async () => {
      if (success === 'true') {
        await handleSuccessParam();
      } else if (canceled === 'true') {
        setPaymentStatus('error');
        setMessage('Payment was canceled. You can try again when you\'re ready.');
        await fetchPrintJob();
      } else {
        // No parameters, just fetch the job and check its status
        const jobData = await fetchPrintJob();
        
        if (!jobData) {
          setPaymentStatus('error');
          setMessage('Payment information is missing.');
        }
      }
    };
    
    init();
  }, [jobId, searchParams, navigate]);

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="text-center mb-10">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Status</h1>
        <p className="text-gray-600">Print Job #{jobId}</p>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-6">
          {paymentStatus === 'loading' ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mb-4"></div>
              <p className="text-gray-600">Checking payment status...</p>
            </div>
          ) : paymentStatus === 'success' ? (
            <div className="text-center py-8">
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Successful!</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="bg-gray-50 p-4 rounded-md mb-6 max-w-md mx-auto">
                <h3 className="font-medium text-gray-700 mb-2">What happens next?</h3>
                <ul className="text-sm text-gray-600 text-left list-disc pl-5 space-y-1">
                  <li>Your print job is now in the production queue</li>
                  <li>You'll receive email updates as your order progresses</li>
                  <li>You can check the status anytime on the Print Jobs page</li>
                  <li>Shipping typically takes 7-10 business days</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Failed</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="bg-red-50 p-4 rounded-md mb-6 max-w-md mx-auto">
                <h3 className="font-medium text-gray-700 mb-2">What to do next:</h3>
                <ul className="text-sm text-gray-600 text-left list-disc pl-5 space-y-1">
                  <li>Check your payment details and try again</li>
                  <li>Ensure your card has sufficient funds</li>
                  <li>Contact your bank if payments are being declined</li>
                  <li>Try a different payment method</li>
                </ul>
              </div>
              <Link
                to={`/print-jobs/${jobId}`}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Back to Print Job
              </Link>
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-4 flex justify-center">
          <Link
            to={`/account/print-jobs/${jobId}`}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-4"
          >
            Back to Print Job
          </Link>
          <Link
            to="/account/print-jobs"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            View All Print Jobs
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrintJobPaymentStatusPage;
